import React, { useState } from 'react';
import { Drawer, DrawerHeader, DrawerContent, DrawerFooter, RegularButton, Typography } from 'fronton-react';
import { ChevronRightIcon } from '@fronton/icons-react';
import { useTranslation } from 'react-i18next';
import { SetProduct } from 'features/product-info/types';
import { ProductCard } from '../product-card';
import { Image } from './image';
import { drawerButton, drawerContent, stocksButton, root, gallery, galleryItem } from './styles';
import { analyticsUtils } from 'utils';
import { HiddentProductList } from './hidden-product-list';

type Props = {
  addToCart: () => void;
  eligible: boolean;
  products: SetProduct[];
};

export const DATA_QA_SET_GALLERY = 'set-gallery';
export const ITEMS_TO_SHOW = 4;

export function Gallery({ addToCart, eligible, products }: Props) {
  const [open, setOpen] = useState(false);

  const { t } = useTranslation();

  if (!products?.length) return null;

  const showMore = products.length > ITEMS_TO_SHOW ? `+${products.length - ITEMS_TO_SHOW}` : '';

  const textShowMoreButton = `${products.length} ${t('product-info.set.stocks-count.count', { count: products.length })} ${t('product-info.set.stocks')}`;

  function handleOpen() {
    setOpen((isOpen) => !isOpen);
    if (!open) {
      analyticsUtils.sendEvent('GA:clickButton', {
        elementType: 'button',
        elementBlock: 'komplektyDetail',
        elementName: 'productsInKomplekty',
        elementText: textShowMoreButton,
      });
    }
  }

  return (
    <>
      <div className={root} onClick={handleOpen} data-qa={DATA_QA_SET_GALLERY}>
        <RegularButton
          size="m"
          variant="pseudo"
          className={stocksButton}
          iconRight={<ChevronRightIcon type="outline" size="l" color="text-primary" />}
        >
          {textShowMoreButton}
        </RegularButton>
        <div className={gallery}>
          {products.slice(0, ITEMS_TO_SHOW).map(({ productId, data: { mediaMainPhoto, displayedName } }) => (
            <div className={galleryItem} key={productId}>
              <Image src={mediaMainPhoto?.desktop ?? mediaMainPhoto} alt={displayedName || ''} />
            </div>
          ))}
          {showMore && (
            <div className={galleryItem}>
              <Typography size="body_long" variant="m">
                {showMore}
              </Typography>
            </div>
          )}
        </div>
      </div>
      <Drawer breakpoint={320} onClose={handleOpen} show={open} size="m">
        <DrawerHeader onClickBack={handleOpen} title={t('product-info.set.title')} />
        <DrawerContent>
          <div className={drawerContent}>
            {products.map(({ productId, quantity, data }, index) => (
              <ProductCard key={productId} quantity={quantity} {...data} index={index} />
            ))}
          </div>
        </DrawerContent>
        <DrawerFooter>
          {eligible ? (
            <RegularButton
              onClick={addToCart}
              className={drawerButton}
              size="l"
              variant="primary"
              href="/basket/"
              wide
            >
              {t('product-info.set.buy')}
            </RegularButton>
          ) : (
            <RegularButton onClick={handleOpen} className={drawerButton} size="l" variant="secondary" wide>
              {t('ui.buttons.close')}
            </RegularButton>
          )}
        </DrawerFooter>
      </Drawer>
      <HiddentProductList products={products} />
    </>
  );
}
